import React from 'react';
import { XMarkIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';

interface SubMenuItem {
  label: string;
  href: string;
}

interface MenuItem {
  label: string;
  href?: string;
  subItems?: SubMenuItem[];
}

interface MobileNavigationProps {
  menuItems: MenuItem[];
  currentPath: string;
  isMobileMenuOpen: boolean;
  activeMobileDropdown: string | null;
  onCloseMobileMenu: () => void;
  onToggleMobileDropdown: (label: string) => void;
  isActiveItem: (item: MenuItem) => boolean;
}

const MobileNavigation: React.FC<MobileNavigationProps> = ({
  menuItems,
  currentPath,
  isMobileMenuOpen,
  activeMobileDropdown,
  onCloseMobileMenu,
  onToggleMobileDropdown,
  isActiveItem,
}) => {
  return (
    <>
      {/* Mobile Navigation Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={onCloseMobileMenu}
        />
      )}

      {/* Mobile Navigation Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-80 transform transition-transform duration-300 ease-in-out z-50 md:hidden bg-primary-500 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Mobile Header */}
        <div className="flex items-center justify-end p-4 h-20 md:h-24 border-b bg-primary-500 border-b-secondary-500">
          <button
            onClick={onCloseMobileMenu}
            className="p-2 rounded-lg text-secondary-500 hover:opacity-80 transition-colors duration-200 cursor-pointer"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Mobile Menu Items */}
        <div className="py-4 px-2 overflow-y-auto h-full">
          <div className="space-y-1">
            {menuItems.map((item) => (
              <div key={item.label}>
                {item.href && !item.subItems ? (
                  <Link
                    to={item.href}
                    className={`block px-6 py-4 rounded-lg text-base font-medium transition-colors duration-200 cursor-pointer ${
                      isActiveItem(item)
                        ? 'bg-secondary-500 text-primary-500'
                        : 'text-secondary-500 hover:opacity-80'
                    }`}
                    onClick={onCloseMobileMenu}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <>
                    {item.href && item.subItems ? (
                      <div
                        className={`flex items-center rounded-lg transition-colors duration-200 ${
                          isActiveItem(item)
                            ? 'bg-secondary-500 text-primary-500'
                            : 'text-secondary-500 hover:opacity-80'
                        }`}
                      >
                        <Link
                          to={item.href}
                          className="flex-1 px-6 py-4 text-base font-medium cursor-pointer"
                          onClick={onCloseMobileMenu}
                        >
                          {item.label}
                        </Link>
                        <button
                          onClick={() => onToggleMobileDropdown(item.label)}
                          className="px-4 py-4 text-base font-medium transition-colors duration-200 cursor-pointer"
                        >
                          <ChevronDownIcon
                            className={`w-4 h-4 transition-transform duration-200 ${
                              activeMobileDropdown === item.label
                                ? 'rotate-180'
                                : ''
                            }`}
                          />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => onToggleMobileDropdown(item.label)}
                        className={`w-full flex items-center justify-between px-6 py-4 rounded-lg text-base font-medium transition-colors duration-200 cursor-pointer ${
                          isActiveItem(item)
                            ? 'bg-secondary-500 text-primary-500'
                            : 'text-secondary-500 hover:opacity-80'
                        }`}
                      >
                        <span>{item.label}</span>
                        <ChevronDownIcon
                          className={`w-4 h-4 transition-transform duration-200 ${
                            activeMobileDropdown === item.label
                              ? 'rotate-180'
                              : ''
                          }`}
                        />
                      </button>
                    )}

                    {/* Mobile Dropdown */}
                    {item.subItems && (
                      <div
                        className={`overflow-hidden transition-all duration-300 ease-in-out ${
                          activeMobileDropdown === item.label
                            ? 'max-h-96 opacity-100'
                            : 'max-h-0 opacity-0'
                        }`}
                      >
                        <div className="ml-4 mt-1 space-y-1">
                          {item.subItems.map((subItem) => (
                            <Link
                              key={subItem.label}
                              to={subItem.href}
                              className={`block px-6 py-3 rounded-lg text-base transition-colors duration-200 cursor-pointer text-secondary-500 ${
                                currentPath === subItem.href
                                  ? 'bg-secondary-100 border-l-2 border-l-secondary-500'
                                  : 'hover:opacity-80'
                              }`}
                              onClick={onCloseMobileMenu}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileNavigation;
